import { memo, type FC } from 'react';
import { CounterCard, CounterButton } from '../ui';
import type { Camera } from '@smolt/shared/types';
import { formatWeight } from '@smolt/shared/utils';
import { SessionDisplay } from '@smolt/shared/models';

export interface CounterStatusCardProps {
  camera: Camera;
  onSettingsClick: () => void;
  onStartStop: () => void;
  tonnesPerHour?: number;
  fishPerHour?: number;
  isMobile?: boolean;
}

/**
 * CounterStatusCard - Counter Page Status Card Component
 * Displays camera status metrics and action buttons
 * Memoized to prevent unnecessary re-renders when camera object identity is unchanged
 */
export const CounterStatusCard: FC<CounterStatusCardProps> = memo(
  ({
    camera,
    onSettingsClick,
    onStartStop,
    tonnesPerHour = 0,
    fishPerHour = 0,
    isMobile = false,
  }) => {
    // Convert camera to SessionDisplay for business logic
    const sessionDisplay = new SessionDisplay(camera);

    // Check if setup is complete
    const isSetupComplete =
      camera.inputTank && camera.outputTank && camera.weightPerFish && camera.weightPerFish > 0;

    // Tank display text
    const tankDisplayText = isSetupComplete
      ? `${camera.inputTank} → ${camera.outputTank}`
      : 'Setup Required';

    return (
      <div style={{ height: 'auto', containerType: isMobile ? 'normal' : 'size' }}>
        <CounterCard
          variant="light"
          padding="none"
          className=""
        >
          <div
            className="flex flex-col"
            style={{ padding: '4px 8px' }}
          >
            {/* Status Label + Tank Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                marginBottom: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(8px, 1.6cqh, 20px)',
                minHeight: isMobile ? 'clamp(18px, 5.7vw, 27px)' : 'clamp(18px, 4.5cqi, 30px)',
              }}
            >
              {/* Status Label - Left */}
              <div
                style={{
                  fontFamily: 'Noto Sans',
                  fontWeight: 700,
                  fontSize: isMobile ? 'clamp(12px, 3.8vw, 18px)' : 'clamp(12px, 3cqi, 20px)',
                  lineHeight: isMobile ? 'clamp(18px, 5.7vw, 27px)' : 'clamp(18px, 4.5cqi, 30px)',
                  letterSpacing: '0%',
                  textAlign: 'left',
                  color: '#8E8E93',
                }}
              >
                Status
              </div>

              {/* Tank Info - Centered */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontFamily: 'Noto Sans',
                  fontWeight: 700,
                  fontSize: isMobile ? 'clamp(12px, 3.8vw, 17px)' : 'clamp(12px, 3.0cqi, 20px)',
                  lineHeight: isMobile ? 'clamp(15px, 4.8vw, 23px)' : 'clamp(15px, 3.8cqi, 26px)',
                  color: '#111827',
                  textAlign: 'center',
                }}
              >
                {tankDisplayText}
              </div>
            </div>

            {/* Primary Stats and Action Buttons wrapper */}
            <div
              className="flex flex-col"
              style={{
                flex: '0 0 auto',
              }}
            >
              {/* Primary Stats - Labels and values on separate rows */}
              <div
                className="grid grid-cols-3 text-center"
                style={{
                  flex: '0 0 auto',
                  columnGap: '8px',
                  rowGap: isMobile ? '4px' : 'clamp(4px, 1cqh, 12px)',
                  marginBottom: isMobile ? '20px' : 'clamp(8px, 3cqh, 24px)',
                }}
              >
                {/* Row 1: Labels */}
                <div
                  className="text-[#8E8E93] flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(11px, 3.8vw, 16px)' : 'clamp(12px, 3cqi, 20px)',
                  }}
                >
                  Count & Time
                </div>
                <div
                  className="text-[#8E8E93] flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(11px, 3.8vw, 16px)' : 'clamp(12px, 3cqi, 20px)',
                  }}
                >
                  Weight
                </div>
                <div
                  className="text-[#8E8E93] flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(11px, 3.8vw, 16px)' : 'clamp(12px, 3cqi, 20px)',
                  }}
                >
                  Real-time Speed
                </div>

                {/* Row 2: Primary Values */}
                <div
                  className="text-fs-text-primary font-bold leading-none flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(16px, 5.5vw, 24px)' : 'clamp(20px, 5cqi, 36px)',
                  }}
                >
                  {sessionDisplay.isCounting
                    ? Math.floor(camera.count).toLocaleString()
                    : sessionDisplay.getStatusText()}
                </div>
                <div
                  className="text-fs-text-primary font-bold leading-none flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(16px, 5.5vw, 24px)' : 'clamp(20px, 5cqi, 36px)',
                  }}
                >
                  {sessionDisplay.isCounting
                    ? formatWeight(((camera.weightPerFish ?? 0) * camera.count) / 1000)
                    : '0.00'}{' '}
                  t
                </div>
                <div
                  className="text-fs-text-primary font-bold leading-none flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(16px, 5.5vw, 24px)' : 'clamp(20px, 5cqi, 36px)',
                  }}
                >
                  {sessionDisplay.isCounting ? tonnesPerHour.toFixed(2) : '0.00'} t/h
                </div>

                {/* Row 3: Secondary Values */}
                <div
                  className="text-[#1C1C1E] flex items-center justify-center font-mono"
                  style={{
                    fontSize: isMobile ? 'clamp(11px, 3.8vw, 16px)' : 'clamp(12px, 3cqi, 20px)',
                    fontFamily: 'Noto Sans',
                  }}
                >
                  {sessionDisplay.isCounting ? camera.duration : '00:00:00'}
                </div>
                <div
                  className="text-[#1C1C1E] flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(11px, 3.8vw, 16px)' : 'clamp(12px, 3cqi, 20px)',
                  }}
                >
                  {camera.weightPerFish ? `${camera.weightPerFish.toFixed(2)} kg/fish` : '-'}
                </div>
                <div
                  className="text-[#1C1C1E] flex items-center justify-center"
                  style={{
                    fontSize: isMobile ? 'clamp(11px, 3.8vw, 16px)' : 'clamp(12px, 3cqi, 20px)',
                  }}
                >
                  {sessionDisplay.isCounting ? Math.round(fishPerHour).toLocaleString() : '0'}{' '}
                  fish/h
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="flex justify-around"
                style={
                  isMobile
                    ? { paddingBottom: '12px' }
                    : {
                        paddingTop: '20px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        paddingBottom: '20px',
                      }
                }
              >
                <CounterButton
                  variant="settings"
                  onClick={onSettingsClick}
                  style={
                    isMobile
                      ? {
                          width: 'clamp(110px, 37.5vw, 150px)',
                          height: 'clamp(28px, 9.4vw, 38px)',
                          padding: '0',
                          fontSize: 'clamp(11px, 3.8vw, 15px)',
                        }
                      : {
                          width: 'clamp(128px, 32cqi, 200px)',
                          height: 'clamp(32px, 8cqi, 50px)',
                          padding: '0',
                          fontSize: 'clamp(13px, 3.25cqi, 20px)',
                        }
                  }
                >
                  Counter Settings
                </CounterButton>
                <CounterButton
                  variant={sessionDisplay.isCounting ? 'stop' : 'start'}
                  onClick={onStartStop}
                  disabled={sessionDisplay.isTransitioning}
                  style={
                    isMobile
                      ? {
                          width: 'clamp(110px, 37.5vw, 150px)',
                          height: 'clamp(28px, 9.4vw, 38px)',
                          padding: '0',
                          fontSize: 'clamp(11px, 3.8vw, 15px)',
                        }
                      : {
                          width: 'clamp(128px, 32cqi, 200px)',
                          height: 'clamp(32px, 8cqi, 50px)',
                          padding: '0',
                          fontSize: 'clamp(13px, 3.25cqi, 20px)',
                        }
                  }
                >
                  {sessionDisplay.isCounting ? 'Stop Counting' : 'Start Counting'}
                </CounterButton>
              </div>
            </div>
          </div>
        </CounterCard>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if camera object identity changed or handlers changed
    // React Query's structural sharing ensures camera object reference is stable when data hasn't changed
    return (
      prevProps.camera === nextProps.camera &&
      prevProps.onSettingsClick === nextProps.onSettingsClick &&
      prevProps.onStartStop === nextProps.onStartStop &&
      prevProps.tonnesPerHour === nextProps.tonnesPerHour &&
      prevProps.fishPerHour === nextProps.fishPerHour &&
      prevProps.isMobile === nextProps.isMobile
    );
  }
);

CounterStatusCard.displayName = 'CounterStatusCard';
